/* global URL_AGENT_CHAT */
import { Dispatch, FormEvent, RefObject, SetStateAction, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@context/AuthContext';
import useTracking, { TRACK_EVENT_NAMES } from '@utils/hooks/useTracking';
import { resolveAgentRoute } from './agentRouteMap';
import { consumeOpenOnMount } from './openOnMount';

/**
 * usePaulinaChat — toda la lógica del chat con Paulina (asistente de soporte/CS).
 *
 * Extraído 1:1 de AgentChatWidget para reutilizarlo desde distintas superficies
 * (pill desktop, bottom-sheet móvil) sin duplicar comportamiento. Habla con el
 * Worker de Cloudflare (plick-agent-pc): POST /api/chat para enviar y GET
 * /api/chat/:id por polling hasta obtener la respuesta. El Worker valida la firma
 * del JWT de sesión del panel (el mismo token de localStorage que usan las APIs) —
 * la identidad nunca viaja en el body.
 *
 * Es un refactor PURO: el hook expone el estado y las acciones que antes vivían
 * inline en el componente, con el mismo aislamiento multi-tenant (storageKey por
 * comercio+usuario + effect de sesionId), la misma telemetría pau_* y el mismo
 * contrato ACCION_IR por llave (resolveAgentRoute).
 */

export interface ChatTurn {
  role: 'user' | 'assistant';
  content: string;
  // Identidad ESTABLE de la respuesta (solo turnos del asistente): se asigna al CREARSE el turno
  // y se persiste con el historial, así el voto 👍/👎 pertenece a ESA respuesta (su identidad), no
  // a su posición en la lista. Sin esto el voto se keyaba por índice y "saltaba" de mensaje al
  // recortar el historial o iniciar una conversación nueva (que arranca en idx 0,1,2…). Sobrevive
  // un reload de la MISMA conversación (viaja en sessionStorage con el turno) y nace SIN votar en
  // cada respuesta nueva. Los turnos del usuario no se votan, así que no lo necesitan.
  id?: string;
}

// Kill-switch central de los NUDGES/TIPS PROACTIVOS de Paulina (la burbuja que se auto-muestra,
// p.ej. "Cobra en línea 💳 … ¿Te muestro cómo?", y el nudge de rageclick). Apagado a propósito:
// con `false`, `nudge` nunca se setea, así que NINGUNA superficie proactiva aparece — ni la
// speech-bubble de desktop (PaulinaNudgeBubble), ni el teaser/dot del avatar en móvil
// (PaulinaNudgeTeaserMobile en MobileTopBarV4), ni el banner/card dentro del chat
// (PaulinaConversation). El CHAT MANUAL de Paulina NO se toca: la pill/avatar siguen abriendo el
// chat, el envío/recepción de mensajes y la invitación de rollout ("Conoce a Paulina") siguen
// igual. Para RE-ENCENDER los tips proactivos: cambiar a `true` (un solo lugar, sin más cambios).
export const PAULINA_NUDGES_ENABLED: boolean = false;

export interface Nudge {
  id: string;
  tipo: string;
  // Prioridad del backend (mayor = más importante). El payload de /api/hints la incluye
  // (computeHints la usa para ordenar). En el front la usamos como criterio secundario para
  // clasificar un nudge como "tip de descubrimiento" cuando el tipo no lo deja claro.
  prioridad?: number;
  titulo: string;
  mensaje: string;
  cta:
    | { tipo: 'navegar'; path: string; seccion: string; label: string }
    | { tipo: 'escalar'; link: string; label: string }
    | { tipo: 'chat'; prompt: string; label: string };
}

// ¿Es un nudge de DESCUBRIMIENTO (tip de enganche), que NO debe interrumpir proactivamente a
// quien ya está conversando? El discriminador primario es `tipo === 'descubrimiento'`; como
// salvaguarda usamos también `prioridad < 50` (los tips nacen con prioridad 20 en el backend,
// los nudges relevantes con ≥70), por si llegara un tip con otro `tipo` o sin prioridad explícita.
// Los nudges relevantes (setup_incompleto/diagnostico/bienvenida, prioridad ≥ 50) NO son tips:
// siguen surgiendo aunque haya historial. El rageclick es contextual (no viene de /api/hints) y
// tampoco cae aquí. Ver el filtro en el fetch de hints.
export const esTipDescubrimiento = (n: Nudge): boolean =>
  n.tipo === 'descubrimiento' || (typeof n.prioridad === 'number' && n.prioridad < 50);

// Telemetría homologada al pipeline canónico (igual que Smart Menu): los eventos de Paulina
// viven en TRACK_EVENT_NAMES (catálogo central, nombres `Paulina - …`) y se emiten con
// `trackEvent` de useTracking() → fan-out a PostHog (capture) + Clarity (Smart Event) + GA4
// (snake_case, sin PII). La sesión de Paulina se segmenta por estos eventos, no por tags bespoke.

const POLL_INTERVAL_MS = 1000;
const POLL_TIMEOUT_MS = 90000;
const MAX_HISTORY = 20;
// Lectura defensiva de localStorage: en navegación privada o iframes sandboxed el acceso a
// localStorage puede lanzar SecurityError. Como estas lecturas ocurren en el render (storageKey,
// sesionId), una excepción no atrapada tumbaría el árbol de React. Devolvemos '' ante fallo,
// consistente con el resto del módulo que protege el storage con try/catch.
const safeLocal = (key: string): string => {
  try {
    return localStorage.getItem(key) ?? '';
  } catch {
    return '';
  }
};

// Sufijo de identidad (comercio, usuario) para namespacear metadatos por sesión. Centraliza la
// lectura de id_commerce/id_user con el mismo fallback 'anon' que storageKey, para que historial,
// cooldowns de nudges (pau_nudge_) y gap de re-evaluación de hints (pau_hints_last_eval) compartan
// EXACTAMENTE la misma identidad y aislamiento.
const identitySuffix = (): string => {
  const c = safeLocal('id_commerce') || 'anon';
  const u = safeLocal('id_user') || 'anon';
  return `${c}:${u}`;
};

// Aislamiento multi-tenant: la copia de DISPLAY del historial se guarda por
// (comercio, usuario), nunca en una clave global — así un login distinto en la
// misma pestaña jamás lee la conversación del negocio anterior. La verdad de la
// conversación vive server-side; esto es solo el caché de pantalla.
const storageKey = (): string => `agent_chat_history:${identitySuffix()}`;

// Id de la SESIÓN de chat (la conversación en curso), namespaceado por identidad. Distinto del
// "sesionId" de identidad (comercio:usuario): este identifica UNA conversación concreta y cambia
// cuando se inicia una nueva (clearConversation lo regenera). Lo usa la llave de persistencia de
// votos (PaulinaConversation) para que una conversación NUEVA nazca limpia (no herede los votos de
// la anterior) y para que un reload de la MISMA conversación los conserve. Conserva el prefijo
// 'agent_chat_' para que el barrido de logout (startsWith('agent_chat_…')) lo siga matcheando.
const sessionIdKey = (): string => `agent_chat_session:${identitySuffix()}`;
// Lee el id de sesión persistido o crea uno nuevo (estable: se persiste, así sobrevive un reload).
const ensureSessionId = (): string => {
  try {
    const existing = sessionStorage.getItem(sessionIdKey());
    if (existing) return existing;
    const fresh = `s-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    sessionStorage.setItem(sessionIdKey(), fresh);
    return fresh;
  } catch {
    // Sin storage: id efímero por render. Los votos no persistirán, pero el chat funciona.
    return `s-${Date.now()}`;
  }
};
// Regenera el id de sesión: lo llama clearConversation para que la siguiente conversación arranque
// con votos limpios (la llave de votos cambia → no lee los de la conversación recién limpiada).
const resetSessionId = (): string => {
  try {
    const fresh = `s-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    sessionStorage.setItem(sessionIdKey(), fresh);
    return fresh;
  } catch {
    return `s-${Date.now()}`;
  }
};

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

// Id estable y único por turno del asistente. Monotónico dentro de la pestaña + timestamp y un
// contador, para que dos respuestas creadas en el mismo milisegundo (envíos rápidos sucesivos) no
// colisionen. No necesita ser global ni persistido como contador: una vez asignado, el id viaja
// EN el turno (sessionStorage), así que tras un reload las respuestas conservan su identidad y el
// voto sigue matcheando. Solo importa que sea único entre las respuestas vivas en la conversación.
let turnSeq = 0;
const nextTurnId = (): string => `a-${Date.now()}-${turnSeq++}`;

// CAVEAT de restore-session (H2 — riesgo BAJO, documentado a propósito, SIN cleanup automático):
// el historial de DISPLAY se persiste en sessionStorage, que normalmente muere al cerrar la
// pestaña. PERO los navegadores (Chrome/Firefox) pueden REHIDRATAR sessionStorage vía "restaurar
// sesión / reabrir pestañas cerradas" tras un cierre SIN logout, resucitando esta copia. En un
// dispositivo COMPARTIDO el siguiente usuario podría ver el historial del anterior si reabre la
// pestaña restaurada. NO añadimos un listener pagehide/beforeunload para borrarlo: borraría la
// conversación en navegaciones/refresh normales (mal trade-off, peor UX para el caso común). La
// mitigación correcta es LOGOUT, que sí barre estas claves (ver AuthContext.logout). Riesgo bajo:
// es un caché de pantalla de soporte (la verdad vive server-side), no dato bancario.
const loadHistory = (): ChatTurn[] => {
  try {
    const raw = sessionStorage.getItem(storageKey());
    return raw ? (JSON.parse(raw) as ChatTurn[]) : [];
  } catch {
    return [];
  }
};

const saveHistory = (turns: ChatTurn[]) => {
  try {
    sessionStorage.setItem(storageKey(), JSON.stringify(turns.slice(-MAX_HISTORY)));
  } catch {
    /* storage lleno o bloqueado: el chat sigue funcionando sin persistencia */
  }
};

// Cadencia: la DURACIÓN del cooldown sigue saliendo del TIPO (un setup_incompleto y un tip de
// descubrimiento "descansan" tiempos distintos). Default 12 h y los cooldowns "largos" (≥24 h)
// bajados a 12 para que un nudge pueda reaparecer ~2× al día sin volverse spam. Se conservan los
// valores intencionalmente más cortos (diagnostico/rageclick) y el centinela `bienvenida`
// (9999 = "una sola vez por identidad", no es una duración a recortar).
const NUDGE_COOLDOWN_H: Record<string, number> = {
  bienvenida: 9999,
  setup_incompleto: 12,
  diagnostico: 6,
  descubrimiento: 12,
  rageclick: 4,
};
// Clave del cooldown namespaceada por identidad y por ID del nudge:
// pau_nudge_${id_commerce}:${id_user}:${id}. ANTES era por TIPO (pau_nudge_…:${tipo}); como todos
// los tips de descubrimiento comparten tipo='descubrimiento', compartían UN solo cooldown → solo se
// mostraba el de mayor prioridad y, tras vencer, el MISMO otra vez (sin rotación). Al keyar por id,
// cada nudge tiene su PROPIA ventana → el de mayor prioridad cuyo id no esté en cooldown rota
// naturalmente con los demás. La DURACIÓN sigue saliendo de NUDGE_COOLDOWN_H[tipo] (lo que cambia
// es la LLAVE → por id). Conserva el prefijo 'pau_nudge_' para que el barrido de logout
// (startsWith) la siga matcheando (H3, aislamiento entre sesiones en la misma pestaña).
const nudgeKey = (id: string): string => `pau_nudge_${identitySuffix()}:${id}`;
const nudgeEnCooldown = (id: string, tipo: string): boolean => {
  try {
    const last = Number(localStorage.getItem(nudgeKey(id)) || 0);
    const h = NUDGE_COOLDOWN_H[tipo] ?? 12;
    return Date.now() - last < h * 3600 * 1000;
  } catch {
    return false;
  }
};
const marcarNudge = (id: string) => {
  try {
    localStorage.setItem(nudgeKey(id), String(Date.now()));
  } catch {
    /* sin storage: se mostrará otra vez, aceptable */
  }
};

// Re-evaluación de hints por TIEMPO (no one-shot por sesión). El throttle anterior
// (`pau_hints_done` en sessionStorage) solo corría una vez por pestaña: los comercios
// que nunca cierran sesión (pestaña siempre abierta) veían el nudge una vez y nunca más.
// Ahora guardamos el último timestamp de evaluación en localStorage (sobrevive la pestaña
// siempre-abierta) y re-evaluamos cada HINTS_REFRESH_H horas. El cooldown por id de nudge
// (NUDGE_COOLDOWN_H da la duración, por tipo) sigue aplicando ENCIMA: aunque re-evaluemos cada 6 h,
// un mismo nudge no se repite antes de su cooldown (12 h por default).
const HINTS_REFRESH_H = 6;
// Clave del gap namespaceada por identidad (H4): pau_hints_last_eval:${id_commerce}:${id_user}.
// Antes era global (pau_hints_last_eval) → un cierre-SIN-logout dejaba que el siguiente usuario
// heredara el gap (no re-evaluaba hints hasta vencer el gap del usuario anterior). Conserva el
// prefijo 'pau_hints_last_eval' para que el barrido de logout (startsWith) la siga matcheando.
const hintsLastEvalKey = (): string => `pau_hints_last_eval:${identitySuffix()}`;
// ¿Toca re-evaluar hints? true si nunca se evaluó o si pasó el gap de HINTS_REFRESH_H.
const hintsDebenReevaluarse = (): boolean => {
  try {
    const last = Number(localStorage.getItem(hintsLastEvalKey()) || 0);
    return Date.now() - last > HINTS_REFRESH_H * 3600 * 1000;
  } catch {
    // Sin storage: evaluamos (no podemos garantizar el gap, mejor pecar de proactivos).
    return true;
  }
};
const marcarHintsEvaluados = () => {
  try {
    localStorage.setItem(hintsLastEvalKey(), String(Date.now()));
  } catch {
    /* sin storage: se re-evaluará en el próximo render, aceptable */
  }
};
// Resetea el gap para que la identidad ACTUAL re-evalúe de inmediato (no herede el gap
// del comercio/sucursal anterior). Lo llama el effect de aislamiento por sesionId.
const resetHintsGap = () => {
  try {
    localStorage.removeItem(hintsLastEvalKey());
  } catch {
    /* sin storage: hintsDebenReevaluarse() devolverá true igual */
  }
};

export interface PaulinaChat {
  /** ¿El agente debe renderizarse? (auth + rol + URL configurada). */
  enabled: boolean;
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  turns: ChatTurn[];
  input: string;
  setInput: Dispatch<SetStateAction<string>>;
  busy: boolean;
  error: string;
  nudge: Nudge | null;
  /** Id de la conversación en curso (cambia al limpiar/cambiar de identidad). Lo usa la llave de
   *  persistencia de votos para que una conversación nueva nazca sin votos heredados. */
  sesionChatId: string;
  bottomRef: RefObject<HTMLDivElement>;
  /** Abre/cierra el chat instrumentando pau_chat_abierto al abrir. */
  toggleOpen: () => void;
  /** Navega a una sección por llave (contrato ACCION_IR → resolveAgentRoute). */
  goTo: (seccion: string) => void;
  aceptarNudge: (n: Nudge) => void;
  descartarNudge: (n: Nudge) => void;
  clearConversation: () => void;
  /** Submit del formulario de input (envía input.trim()). */
  send: (e: FormEvent) => void;
  /** Envío directo de un mensaje (usado por nudges de tipo chat). */
  doSend: (message: string) => Promise<void>;
}

export const usePaulinaChat = (): PaulinaChat => {
  const { isAuthenticated, isDeliveryPerson, user, commerceName } = useAuth();
  const { trackEvent } = useTracking();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [turns, setTurns] = useState<ChatTurn[]>(loadHistory);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [nudge, setNudge] = useState<Nudge | null>(null);
  // Id de la conversación en curso. Se inicializa con el persistido (o uno nuevo) para que un reload
  // de la MISMA conversación conserve los votos; se regenera al limpiar o cambiar de identidad.
  const [sesionChatId, setSesionChatId] = useState<string>(ensureSessionId);
  const bottomRef = useRef<HTMLDivElement>(null);

  const enabled = !(!isAuthenticated || isDeliveryPerson || typeof URL_AGENT_CHAT === 'undefined' || !URL_AGENT_CHAT);

  // Aislamiento entre sesiones/sucursales: al cambiar de identidad (logout→login O
  // cambio de sucursal vía refreshLogin — ambos SPA sin reload), recargar el historial
  // namespaceado de la sesión ACTUAL y cerrar el chat, para no arrastrar la conversación
  // de otro negocio/sucursal en pantalla ni enviarla al backend.
  const sesionId = `${safeLocal('id_commerce')}:${safeLocal('id_user')}`;
  useEffect(() => {
    setTurns(loadHistory());
    setOpen(false);
    setNudge(null);
    // Recarga el id de sesión de la identidad ACTUAL (su valor persistido namespaceado), para que
    // los votos se lean/escriban contra la conversación de ESTA identidad y no la anterior.
    setSesionChatId(ensureSessionId());
    // El gap de hints (pau_hints_last_eval) es GLOBAL (no namespaceado): el cambio de sucursal
    // vía refreshLogin es SPA (sin reload) y no lo purga, así que lo reseteamos aquí para que la
    // sucursal/usuario destino re-evalúe nudges de INMEDIATO (no herede el gap del anterior).
    // CRÍTICO para el aislamiento entre sucursales/usuarios: sin esto, un comercio nuevo en la
    // misma pestaña podría quedar sin nudges hasta que venza el gap del comercio anterior.
    // logout ya barre localStorage por separado.
    resetHintsGap();
  }, [sesionId]);

  useEffect(() => {
    if (open) bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [open, turns, busy]);

  // Rol de activación: pide nudges proactivos al Worker. Re-evaluación por TIEMPO (cada
  // HINTS_REFRESH_H horas), no one-shot por sesión: así la pestaña siempre-abierta vuelve a
  // recibir nudges. El cooldown por id (duración de NUDGE_COOLDOWN_H[tipo]) sigue aplicando encima.
  useEffect(() => {
    // Tips proactivos apagados (kill-switch): no pedimos hints ni emitimos telemetría proactiva.
    if (!PAULINA_NUDGES_ENABLED) return;
    if (!isAuthenticated || isDeliveryPerson) return;
    if (typeof URL_AGENT_CHAT === 'undefined' || !URL_AGENT_CHAT) return;
    // Gap por tiempo en localStorage: solo hacemos el GET /api/hints si pasó el refresh.
    if (!hintsDebenReevaluarse()) return;
    // Marcamos el timestamp ANTES del fetch (no después) para no re-disparar la petición en
    // renders concurrentes / doble-montaje de StrictMode mientras el GET está en vuelo.
    marcarHintsEvaluados();
    const token = localStorage.getItem('token');
    if (!token) return;
    (async () => {
      try {
        const res = await fetch(`${URL_AGENT_CHAT}/api/hints`, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) return;
        const { nudges } = (await res.json()) as { nudges: Nudge[] };
        // Con conversación previa (historial no vacío) NO interrumpimos con un TIP de
        // descubrimiento: ese nudge es para enganchar a usuarios nuevos/inactivos, no para
        // cortar a quien ya está conversando. Los nudges RELEVANTES (setup_incompleto/
        // diagnostico/bienvenida, prioridad ≥ 50) sí pueden surgir aunque haya historial —
        // son importantes — respetando cooldown y cesión ante pedidos (sin cambios). Usamos
        // `turns` (no `turns.length` en deps) porque este effect solo corre al re-evaluar hints
        // por tiempo/identidad; leer el historial actual aquí es la señal correcta.
        const hayConversacion = turns.length > 0;
        // Elegimos el nudge de MAYOR prioridad cuyo id NO esté en cooldown (cooldown por id, FIX
        // de rotación): así, con varios tips de descubrimiento de igual tipo pero distinta
        // prioridad, se muestra primero el más importante y, cuando su id entra en cooldown, rota
        // al siguiente — en vez de repetir el mismo (cuando el cooldown era por tipo, todos los
        // tips compartían UNA ventana). Ordenamos por prioridad desc en el front (no dependemos
        // del orden del backend) y respetamos la supresión de tips con conversación previa.
        const elegible = (nudges || [])
          .slice()
          .sort((a, b) => (b.prioridad ?? 0) - (a.prioridad ?? 0))
          .find((n) => !nudgeEnCooldown(n.id, n.tipo) && !(hayConversacion && esTipDescubrimiento(n)));
        if (elegible) {
          setNudge(elegible);
          trackEvent(TRACK_EVENT_NAMES.PaulinaProactiveShown, { tipo: elegible.tipo, id: elegible.id });
        }
      } catch {
        /* sin nudges: experiencia normal */
      }
    })();
    // Incluye sesionId: en un cambio de sucursal (refreshLogin) isAuthenticated no cambia, pero
    // el effect de arriba ya reseteó el gap (resetHintsGap) — re-evaluar aquí re-pide nudges para
    // la sucursal/usuario destino de inmediato. React corre los effects en orden de declaración,
    // así que el gap ya está reseteado cuando llegamos a este check.
  }, [isAuthenticated, isDeliveryPerson, sesionId]);

  // Rage click: 4+ clics rápidos en el mismo punto → ofrecer ayuda.
  useEffect(() => {
    // Tips proactivos apagados (kill-switch): no montamos el detector de rageclick.
    if (!PAULINA_NUDGES_ENABLED) return;
    let count = 0;
    let last = 0;
    let lx = 0;
    let ly = 0;
    const onClick = (e: MouseEvent) => {
      const now = Date.now();
      const cerca = Math.abs(e.clientX - lx) < 40 && Math.abs(e.clientY - ly) < 40;
      count = now - last < 600 && cerca ? count + 1 : 1;
      last = now;
      lx = e.clientX;
      ly = e.clientY;
      if (count >= 4 && !nudgeEnCooldown('rageclick', 'rageclick')) {
        count = 0;
        trackEvent(TRACK_EVENT_NAMES.PaulinaRageClick, { page: window.location.pathname });
        setNudge({
          id: 'rageclick',
          tipo: 'rageclick',
          titulo: '¿Te atoraste? 🤔',
          mensaje: 'Vi que le estás dando varias veces sin que pase nada. ¿Te echo la mano con lo que intentas hacer?',
          cta: {
            tipo: 'chat',
            prompt: 'Estoy batallando con algo en esta pantalla, ¿me ayudas?',
            label: 'Sí, ayúdame →',
          },
        });
      }
    };
    window.addEventListener('click', onClick);
    return () => window.removeEventListener('click', onClick);
  }, []);

  // FIX A — auto-abrir al montar si la invitación dejó la señal one-shot. Al aceptar "Probarla"
  // desde la variante control, el gate pasa a treatment y este hook se MONTA recién entonces; la
  // señal vive en sessionStorage (sobrevive el remonte). consumeOpenOnMount() devuelve true una
  // sola vez y limpia la clave, así el chat se abre solo sin reabrirse en montajes posteriores.
  // El ref guard evita que el doble-montaje de StrictMode lo dispare dos veces.
  const autoOpenDone = useRef(false);
  useEffect(() => {
    if (autoOpenDone.current) return;
    autoOpenDone.current = true;
    if (consumeOpenOnMount()) setOpen(true);
  }, []);

  const doSend = async (message: string) => {
    if (!message || busy) return;

    const token = localStorage.getItem('token');
    if (!token) return;

    const history = turns.slice(-MAX_HISTORY);
    const nextTurns: ChatTurn[] = [...turns, { role: 'user', content: message }];
    setTurns(nextTurns);
    setInput('');
    setBusy(true);
    setError('');
    trackEvent(TRACK_EVENT_NAMES.PaulinaMessageSent, { page: location.pathname, chars: message.length });
    const startedAt = Date.now();

    try {
      const res = await fetch(`${URL_AGENT_CHAT}/api/chat`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message,
          history,
          // Defensa: user.name puede no venir (campo opcional del login);
          // fallback al nombre del comercio para no saludar con un nombre vacío.
          userName: user?.name || commerceName || undefined,
          // Contexto situacional: Paulina sabe en qué sección está el usuario.
          page: location.pathname,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const { chatId } = (await res.json()) as { chatId: string };

      const deadline = Date.now() + POLL_TIMEOUT_MS;
      let reply = '';
      // tools: nombres de las tools usadas por el Worker en este turno (contrato compartido del
      // polling al completar). Se emiten a Clarity/GA4 abajo; vacío → no se emite nada.
      let tools: string[] = [];
      while (Date.now() < deadline) {
        await sleep(POLL_INTERVAL_MS);
        const poll = await fetch(`${URL_AGENT_CHAT}/api/chat/${chatId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!poll.ok) throw new Error(`HTTP ${poll.status}`);
        const data = (await poll.json()) as { status: string; reply?: string; tools?: string[] };
        if (data.status === 'complete') {
          reply = data.reply ?? '';
          tools = data.tools ?? [];
          break;
        }
        if (data.status === 'error') throw new Error('workflow error');
      }
      if (!reply) throw new Error('timeout');

      const finalTurns: ChatTurn[] = [...nextTurns, { role: 'assistant', content: reply, id: nextTurnId() }];
      setTurns(finalTurns);
      saveHistory(finalTurns);
      trackEvent(TRACK_EVENT_NAMES.PaulinaResponseOk, {
        ms: Date.now() - startedAt,
        con_captura: /Captura:\s*http/.test(reply),
        con_navegacion: /ACCION_IR:/.test(reply),
      });
      // Una métrica por cada tool usada en el turno (fan-out vía trackEvent). Si el turno no
      // usó tools, `tools` viene vacío y no se emite nada.
      for (const tool of tools) {
        trackEvent(TRACK_EVENT_NAMES.PaulinaToolUsed, { tool });
      }
    } catch (err) {
      // No revertimos el historial: dejamos visible el mensaje del usuario (ya está en nextTurns)
      // junto al banner de error para que pueda reintentar sin re-escribir. `setTurns(turns)` sería
      // doblemente incorrecto: (1) borraría el mensaje recién enviado, y (2) `turns` es una captura
      // stale del closure — si el usuario cambió de sucursal durante el polling (hasta 90s), pisaría
      // el historial de la sucursal nueva con el de la anterior. El historial solo se persiste en
      // éxito (saveHistory), así que en error no queda nada corrupto en sessionStorage.
      setError('No pude obtener respuesta. Intenta de nuevo en un momento.');
      trackEvent(TRACK_EVENT_NAMES.PaulinaResponseError, {
        motivo: err instanceof Error ? err.message.slice(0, 40) : 'desconocido',
      });
    } finally {
      setBusy(false);
    }
  };

  const goTo = (seccion: string) => {
    const path = resolveAgentRoute(seccion);
    if (!path) return;
    trackEvent(TRACK_EVENT_NAMES.PaulinaNavigationClicked, { seccion, path });
    setOpen(false);
    navigate(path);
  };

  const aceptarNudge = (n: Nudge) => {
    trackEvent(TRACK_EVENT_NAMES.PaulinaProactiveAccepted, { tipo: n.tipo, id: n.id });
    marcarNudge(n.id);
    setNudge(null);
    if (n.cta.tipo === 'navegar') navigate(n.cta.path);
    else if (n.cta.tipo === 'escalar') window.open(n.cta.link, '_blank');
    else {
      setOpen(true);
      doSend(n.cta.prompt);
    }
  };
  const descartarNudge = (n: Nudge) => {
    trackEvent(TRACK_EVENT_NAMES.PaulinaProactiveDismissed, { tipo: n.tipo, id: n.id });
    marcarNudge(n.id);
    setNudge(null);
  };

  const clearConversation = () => {
    trackEvent(TRACK_EVENT_NAMES.PaulinaChatCleared);
    try {
      sessionStorage.removeItem(storageKey());
    } catch {
      /* sin storage no hay nada que borrar */
    }
    // Nueva conversación = nueva sesión de votos: regeneramos el id de sesión para que los próximos
    // turnos arranquen SIN votos heredados (la llave de votos cambia). Los turnos viejos ya nacían
    // con id estable, pero esto garantiza además que la conversación nueva no lea el storage anterior.
    setSesionChatId(resetSessionId());
    setTurns([]);
    setError('');
  };

  const toggleOpen = () => {
    if (!open) trackEvent(TRACK_EVENT_NAMES.PaulinaChatOpened, { page: location.pathname });
    setOpen((v) => !v);
  };

  const send = (e: FormEvent) => {
    e.preventDefault();
    doSend(input.trim());
  };

  return {
    enabled,
    open,
    setOpen,
    turns,
    input,
    setInput,
    busy,
    error,
    // Gate de SALIDA del kill-switch: aun si un nudge se sembró antes de apagar el flag (caché/
    // hidratación) o por una ruta no cubierta por los gates de entrada, aquí lo forzamos a null.
    // Apaga en UN punto todas las superficies proactivas (bubble desktop, teaser/dot móvil, card).
    nudge: PAULINA_NUDGES_ENABLED ? nudge : null,
    sesionChatId,
    bottomRef,
    toggleOpen,
    goTo,
    aceptarNudge,
    descartarNudge,
    clearConversation,
    send,
    doSend,
  };
};
